/**
 * Integration Conditional Parser
 *
 * Parses and validates 'integration' meta-dimension conditions used in normalization conditionals.
 *
 * Supported formats (new preferred format first):
 * - New format: '123|google_ads' or '123|' → { integrationId: 123, nameSlug: 'google_ads' }
 * - Legacy format: 'google_ads#123' or '#123' → { integrationId: 123, nameSlug: 'google_ads' }
 * - Simple format: '123' → { integrationId: 123, nameSlug: null }
 *
 * Note: The name/slug portion is purely decorative for human readability.
 * Only the integration_id is used for matching - the name is NOT validated.
 */

export interface ParsedIntegration {
  integrationId: number
  nameSlug: string | null  // Decorative only - not validated against DB
  /** @deprecated Use nameSlug instead - kept for backward compatibility */
  integratorSlug: string | null
}

export interface IntegrationParseError {
  value: string
  error: string
}

export interface IntegrationValidationError {
  value: string
  error: string
  suggestion?: string
}

/**
 * Parse a single integration value into its components.
 *
 * Supports multiple formats for backward compatibility:
 * - New format: '123|google_ads' or '123|' (ID first, name second)
 * - Legacy format: 'google_ads#123' or '#123' (slug first, ID second)
 * - Simple format: '123' (just ID)
 *
 * @param value - The integration value in any supported format
 * @returns ParsedIntegration object
 * @throws Error if format is invalid
 */
export function parseIntegrationValue(value: string): ParsedIntegration {
  if (!value || value.trim() === '') {
    throw new Error("Empty value")
  }

  const trimmed = value.trim()

  // Detection order: pipe first (new format), then hash (legacy), then simple

  // New format: contains '|' → {id}|{name}
  if (trimmed.includes('|')) {
    const pipeCount = (trimmed.match(/\|/g) || []).length
    if (pipeCount > 1) {
      throw new Error(`Invalid format '${trimmed}': multiple '|' separators not allowed`)
    }

    const parts = trimmed.split('|')
    const idPart = parts[0].trim()
    const namePart = parts[1]?.trim() || null

    if (!idPart) {
      throw new Error(`Invalid format '${trimmed}': missing integration_id before '|'`)
    }

    const id = parseInt(idPart, 10)
    if (isNaN(id)) {
      throw new Error(`Invalid format '${trimmed}': ID portion '${idPart}' must be numeric`)
    }

    return {
      integrationId: id,
      nameSlug: namePart || null,
      integratorSlug: namePart || null  // backward compatibility
    }
  }

  // Legacy format: contains '#' → {slug}#{id}
  if (trimmed.includes('#')) {
    const hashCount = (trimmed.match(/#/g) || []).length
    if (hashCount > 1) {
      throw new Error(`Invalid format '${trimmed}': multiple '#' separators not allowed`)
    }

    const parts = trimmed.split('#')
    const slugPart = parts[0].trim()
    const idPart = parts[1]?.trim()

    // Format: '#123' (just hash and id)
    if (slugPart === '') {
      if (!idPart) {
        throw new Error(`Invalid format '${trimmed}': missing integration_id after '#'`)
      }
      const id = parseInt(idPart, 10)
      if (isNaN(id)) {
        throw new Error(`Invalid format '${trimmed}': ID portion '${idPart}' must be numeric`)
      }
      return {
        integrationId: id,
        nameSlug: null,
        integratorSlug: null
      }
    }

    // Compound format: 'google_ads#123'
    if (!idPart) {
      throw new Error(`Invalid format '${trimmed}': missing integration_id after '#'`)
    }

    const id = parseInt(idPart, 10)
    if (isNaN(id)) {
      throw new Error(`Invalid format '${trimmed}': ID portion '${idPart}' must be numeric`)
    }

    return {
      integrationId: id,
      nameSlug: slugPart,
      integratorSlug: slugPart  // backward compatibility
    }
  }

  // Simple format: just a number (e.g., '123')
  const id = parseInt(trimmed, 10)
  if (isNaN(id)) {
    throw new Error(`Invalid format '${trimmed}': expected '{id}|{name}', '{slug}#{id}', or just '{id}'`)
  }

  return {
    integrationId: id,
    nameSlug: null,
    integratorSlug: null
  }
}

/**
 * Extract all integration references from a conditional string.
 * Looks for patterns like: integration = 'value', integration == 'value',
 * integration != 'value', integration IN ('value1', 'value2')
 *
 * @param conditional - The conditional expression string
 * @returns Array of integration value strings found
 */
export function extractIntegrationReferences(conditional: string): string[] {
  const references: string[] = []

  if (!conditional) return references

  // Pattern for integration = 'value' or integration == 'value' or integration != 'value'
  const equalityPattern = /integration\s*(?:={1,2}|!=)\s*['"]([^'"]+)['"]/gi
  let match

  while ((match = equalityPattern.exec(conditional)) !== null) {
    references.push(match[1])
  }

  // Pattern for integration IN ('value1', 'value2', ...)
  const inPattern = /integration\s+IN\s*\(\s*([^)]+)\s*\)/gi
  while ((match = inPattern.exec(conditional)) !== null) {
    const valuesStr = match[1]
    // Extract individual quoted values
    const valuePattern = /['"]([^'"]+)['"]/g
    let valueMatch
    while ((valueMatch = valuePattern.exec(valuesStr)) !== null) {
      references.push(valueMatch[1])
    }
  }

  return references
}

/**
 * Parse multiple integration references and collect any errors.
 *
 * @param references - Array of integration value strings
 * @returns Object with parsed values and any parse errors
 */
export function parseIntegrationReferences(references: string[]): {
  parsed: ParsedIntegration[]
  errors: IntegrationParseError[]
} {
  const parsed: ParsedIntegration[] = []
  const errors: IntegrationParseError[] = []

  for (const ref of references) {
    try {
      parsed.push(parseIntegrationValue(ref))
    } catch (e: any) {
      errors.push({
        value: ref,
        error: e.message
      })
    }
  }

  return { parsed, errors }
}

/**
 * Find similar integrator slugs for suggestions.
 * Uses simple Levenshtein-like matching.
 *
 * @param input - The invalid integrator slug
 * @param validSlugs - Set of valid integrator slugs
 * @returns Array of similar slugs (max 3)
 */
export function findSimilarIntegrators(input: string, validSlugs: Set<string>): string[] {
  const inputLower = input.toLowerCase()
  const suggestions: { slug: string; score: number }[] = []

  for (const slug of validSlugs) {
    const slugLower = slug.toLowerCase()

    // Check if input is a prefix or suffix
    if (slugLower.startsWith(inputLower) || slugLower.endsWith(inputLower)) {
      suggestions.push({ slug, score: 1 })
      continue
    }

    // Check if input is contained in slug
    if (slugLower.includes(inputLower) || inputLower.includes(slugLower)) {
      suggestions.push({ slug, score: 2 })
      continue
    }

    // Simple character overlap score
    let overlap = 0
    for (const char of inputLower) {
      if (slugLower.includes(char)) overlap++
    }
    const score = 1 - (overlap / Math.max(inputLower.length, slugLower.length))
    if (score < 0.5) {
      suggestions.push({ slug, score: 3 + score })
    }
  }

  return suggestions
    .sort((a, b) => a.score - b.score)
    .slice(0, 3)
    .map(s => s.slug)
}

/**
 * Validate integration references against known integrations.
 *
 * Validation rules:
 * - Format validation always runs (validates numeric ID, proper separators)
 * - Integration ID existence validation runs if validIntegrations is provided
 * - Name/slug portion is NOT validated (it's purely decorative for human readability)
 *
 * @param references - Array of integration value strings to validate
 * @param validIntegrators - Set of valid integrator slugs (kept for backward compatibility, but no longer used for validation)
 * @param validIntegrations - Map of integration_id to integrator_slug (used only for ID existence check)
 * @returns Array of validation errors
 */
export function validateIntegrationReferences(
  references: string[],
  validIntegrators: Set<string>,
  validIntegrations: Map<number, string>
): IntegrationValidationError[] {
  const errors: IntegrationValidationError[] = []
  const hasValidationData = validIntegrations.size > 0

  for (const ref of references) {
    // First, try to parse the reference (format validation - always runs)
    let parsed: ParsedIntegration
    try {
      parsed = parseIntegrationValue(ref)
    } catch (e: any) {
      errors.push({
        value: ref,
        error: e.message
      })
      continue
    }

    // Skip existence validation if no lookup data is available
    if (!hasValidationData) {
      continue
    }

    // Validate integration_id exists
    // Note: We don't validate the name/slug - it's purely decorative
    if (!validIntegrations.has(parsed.integrationId)) {
      errors.push({
        value: ref,
        error: `Integration #${parsed.integrationId} does not exist`
      })
    }
  }

  return errors
}
